/**
 * EntitlementsContext shim. Delegates state + polling + breakdown logic to
 * @wg/hooks/entitlements. Same export names as before; consumers don't move.
 */
import React, { useEffect, useMemo } from 'react';
import {
  EntitlementsProvider as SharedEntitlementsProvider,
  useEntitlements as useSharedEntitlements,
  type EntitlementsBillingApi,
} from '@wg/hooks/entitlements';
import { useAuthContext } from '@wg/hooks/auth';
import billingApi from '../api/billingApi';
import logger from '../utils/logger';

const onForegroundReturn = (cb: () => void): (() => void) => {
  if (typeof document === 'undefined') return () => {};
  const handler = () => {
    if (document.visibilityState === 'visible') cb();
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
};

export const EntitlementsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuthContext();
  const api = useMemo<EntitlementsBillingApi>(
    () => ({
      getUserBalance: (breakdown?: boolean) =>
        billingApi.getUserBalance(breakdown),
    }),
    [],
  );
  // Refresh on auth-state change so signin/guest creation triggers a balance
  // pull even if the React tree didn't unmount the provider.
  useEffect(() => {
    /* shared provider's effect handles initial fetch; nothing to do here. */
  }, [isAuthenticated]);
  return (
    <SharedEntitlementsProvider
      billingApi={api}
      logger={logger}
      isAuthenticated={isAuthenticated}
      onForegroundReturn={onForegroundReturn}
    >
      {children}
    </SharedEntitlementsProvider>
  );
};

export const useEntitlements = useSharedEntitlements;
export type { EntitlementsState } from '@wg/hooks/entitlements';
